import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronLeft,
  ChevronRight,
  Circle,
  Loader2,
  MonitorOff,
  PictureInPicture2,
  Square,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TeleprompterPanel } from '@/components/pitch/TeleprompterPanel';

function formatElapsed(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function useElapsed(startedAt, active, hostWindow) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const win = hostWindow || window;
    setNow(Date.now());
    const id = win.setInterval(() => setNow(Date.now()), 500);
    return () => win.clearInterval(id);
  }, [active, hostWindow]);
  return active && startedAt ? now - startedAt : 0;
}

function StatusBadge({ recording, uploading, sharing, elapsedMs }) {
  if (recording) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-400">
        <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
        REC {formatElapsed(elapsedMs)}
      </span>
    );
  }
  if (uploading) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-300">
        <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-300">
      <span className="h-2 w-2 rounded-full bg-emerald-400" />
      {sharing ? 'Sharing · ready' : 'Ready'}
    </span>
  );
}

function ToolbarButtons({
  compact,
  recording,
  uploading,
  sharing,
  canStart,
  onStart,
  onStop,
  onStopSharing,
}) {
  const size = compact ? 'sm' : 'default';
  return (
    <>
      {recording ? (
        <Button
          type="button"
          size={size}
          variant="destructive"
          className="gap-1.5 rounded-full"
          onClick={onStop}
          data-testid="floating-stop-recording"
        >
          <Square className="h-3.5 w-3.5" /> Stop & upload
        </Button>
      ) : (
        <Button
          type="button"
          size={size}
          className="gap-1.5 rounded-full bg-red-600 hover:bg-red-500 text-white"
          onClick={onStart}
          disabled={!canStart || uploading}
          data-testid="floating-start-recording"
        >
          <Circle className="h-3.5 w-3.5 fill-current" /> Start recording
        </Button>
      )}
      {sharing && !recording ? (
        <Button
          type="button"
          size={size}
          variant="outline"
          className="gap-1.5 rounded-full"
          onClick={onStopSharing}
          data-testid="floating-stop-sharing"
        >
          <MonitorOff className="h-3.5 w-3.5" /> Stop sharing
        </Button>
      ) : null}
    </>
  );
}

function ModuleNav({ label, hasPrev, hasNext, onPrev, onNext, disabled }) {
  return (
    <div className="flex min-w-0 items-center gap-1">
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-7 w-7 shrink-0 rounded-full"
        disabled={disabled || !hasPrev}
        onClick={onPrev}
        aria-label="Previous module"
        data-testid="floating-prev-module"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="min-w-0 truncate text-xs text-slate-300" title={label}>{label}</span>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-7 w-7 shrink-0 rounded-full"
        disabled={disabled || !hasNext}
        onClick={onNext}
        aria-label="Next module"
        data-testid="floating-next-module"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

/**
 * Recording controls that stay reachable while a take or screen share is live.
 * With Document Picture-in-Picture they float above every Chrome tab; otherwise
 * they float at the bottom of this page.
 *
 * PiP lives in another document, so React delegated clicks never fire there —
 * we bridge actions with native listeners on the PiP document.
 */
export function PitchFloatingRecordToolbar({
  visible,
  pipWindow = null,
  pipSupported = false,
  onPopOut,
  recording = false,
  uploading = false,
  sharing = false,
  startedAt = 0,
  moduleLabel = '',
  hasPrev = false,
  hasNext = false,
  onPrev,
  onNext,
  canStart = true,
  onStart,
  onStop,
  onStopSharing,
  teleprompterText = '',
  showTeleprompter = false,
}) {
  const elapsedMs = useElapsed(startedAt, recording, pipWindow);
  const actionsRef = useRef({});
  actionsRef.current = {
    canStart,
    uploading,
    recording,
    onStart,
    onStop,
    onStopSharing,
    onPrev,
    onNext,
  };

  useEffect(() => {
    if (!pipWindow?.document) return undefined;
    const doc = pipWindow.document;
    const onClick = (event) => {
      const target = event.target?.closest?.('[data-testid]');
      if (!target) return;
      const id = target.getAttribute('data-testid');
      const actions = actionsRef.current;
      if (id === 'floating-start-recording') {
        if (!actions.canStart || actions.uploading || actions.recording) return;
        event.preventDefault();
        actions.onStart?.();
      } else if (id === 'floating-stop-recording') {
        event.preventDefault();
        actions.onStop?.();
      } else if (id === 'floating-stop-sharing') {
        event.preventDefault();
        actions.onStopSharing?.();
      } else if (id === 'floating-prev-module') {
        event.preventDefault();
        actions.onPrev?.();
      } else if (id === 'floating-next-module') {
        event.preventDefault();
        actions.onNext?.();
      }
    };
    doc.addEventListener('click', onClick, true);
    return () => doc.removeEventListener('click', onClick, true);
  }, [pipWindow]);

  if (!visible) return null;

  const buttonProps = {
    recording,
    uploading,
    sharing,
    canStart,
    onStart,
    onStop,
    onStopSharing,
  };
  const navProps = {
    label: moduleLabel,
    hasPrev,
    hasNext,
    onPrev,
    onNext,
    disabled: recording || uploading,
  };

  if (pipWindow) {
    return createPortal(
      <div className="dark flex h-screen flex-col gap-2 bg-slate-950 p-3 text-slate-100">
        <div className="flex items-center justify-between gap-2">
          <StatusBadge recording={recording} uploading={uploading} sharing={sharing} elapsedMs={elapsedMs} />
          <ModuleNav {...navProps} />
        </div>
        {showTeleprompter ? (
          <TeleprompterPanel
            text={teleprompterText}
            active={recording}
            speed={36}
            className="min-h-0 flex-1 [&_[data-testid=teleprompter-scroll]]:px-3 [&_[data-testid=teleprompter-scroll]]:py-8 [&_[data-testid=teleprompter-scroll]]:text-base"
          />
        ) : (
          <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg border border-dashed border-slate-700 px-3 text-center text-xs text-slate-500">
            {recording ? 'Recording — switch tabs freely, these controls stay on top.' : 'Controls stay on top of every tab while you record.'}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-center gap-2">
          <ToolbarButtons {...buttonProps} />
        </div>
      </div>,
      pipWindow.document.body,
    );
  }

  return (
    <div
      className="fixed bottom-6 left-1/2 z-[120] flex max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-wrap items-center gap-2 rounded-full border border-slate-700 bg-slate-950/95 px-3 py-2 text-slate-100 shadow-[0_12px_40px_rgba(0,0,0,0.5)] backdrop-blur"
      data-testid="pitch-floating-toolbar"
    >
      <StatusBadge recording={recording} uploading={uploading} sharing={sharing} elapsedMs={elapsedMs} />
      <ModuleNav {...navProps} />
      <ToolbarButtons compact {...buttonProps} />
      {pipSupported ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="gap-1.5 rounded-full text-slate-300"
          onClick={onPopOut}
          title="Keep these controls on top of every tab"
        >
          <PictureInPicture2 className="h-3.5 w-3.5" /> Float over all tabs
        </Button>
      ) : null}
    </div>
  );
}

export default PitchFloatingRecordToolbar;
